import type { DateRange } from "react-day-picker"

import type { SerializedDateRange } from "../../typings"
import { FilterModifier } from "./FilterModifier"

export const DateEqualsFilterModifier = FilterModifier.create<Date, Date, string>({
	id: "date-equals",
	getApplyFn: (filterValue) => {
		const filterTime = filterValue.getTime()
		return (value) => {
			return value.getTime() === filterTime
		}
	},
	render: () => "is",
	serialize: (filterValue) => filterValue.toISOString(),
	deserialize: (filterValue) => new Date(filterValue),
})

export const DateNotEqualsFilterModifier = FilterModifier.create<Date, Date, string>({
	id: "date-not-equals",
	getApplyFn: (filterValue) => {
		const filterTime = filterValue.getTime()
		return (value) => {
			return value.getTime() !== filterTime
		}
	},
	render: () => "is not",
	serialize: (filterValue) => filterValue.toISOString(),
	deserialize: (filterValue) => new Date(filterValue),
})

export const NullableDateEqualsFilterModifier = FilterModifier.create<Date | null, Date | null, string | null>({
	id: "nullable-date-equals",
	getApplyFn: (filterValue) => {
		const filterTime = filterValue?.getTime()
		return (value) => {
			return value?.getTime() === filterTime
		}
	},
	render: () => "is",
	serialize: (filterValue) => filterValue?.toISOString() ?? null,
	deserialize: (filterValue) => (filterValue ? new Date(filterValue) : null),
})

export const NullableDateNotEqualsFilterModifier = FilterModifier.create<Date | null, Date | null, string | null>({
	id: "nullable-date-not-equals",
	getApplyFn: (filterValue) => {
		const filterTime = filterValue?.getTime()
		return (value) => {
			return value?.getTime() !== filterTime
		}
	},
	render: () => "is not",
	serialize: (filterValue) => filterValue?.toISOString() ?? null,
	deserialize: (filterValue) => (filterValue ? new Date(filterValue) : null),
})

export const DateBeforeFilterModifier = FilterModifier.create<Date, Date, string>({
	id: "date-before",
	getApplyFn: (filterValue) => {
		const filterTime = filterValue.getTime()
		return (value) => {
			return value.getTime() < filterTime
		}
	},
	render: () => "before",
	serialize: (filterValue) => filterValue.toISOString(),
	deserialize: (filterValue) => new Date(filterValue),
})

export const DateAfterFilterModifier = FilterModifier.create<Date, Date, string>({
	id: "date-after",
	getApplyFn: (filterValue) => {
		const filterTime = filterValue.getTime()
		return (value) => {
			return value.getTime() > filterTime
		}
	},
	render: () => "after",
	serialize: (filterValue) => filterValue.toISOString(),
	deserialize: (filterValue) => new Date(filterValue),
})

export const DateRangeInFilterModifier = FilterModifier.create<Date, DateRange, SerializedDateRange>({
	id: "date-range-in",
	getApplyFn: (filterValue) => {
		const fromTime = filterValue?.from?.getTime()
		const toTime = filterValue?.to?.getTime()
		return (value) => {
			const valueTime = value.getTime()
			if (fromTime !== undefined && fromTime > valueTime) return false
			if (toTime !== undefined && toTime < valueTime) return false
			return true
		}
	},
	render: () => "in between",
	serialize: (filterValue) => {
		return {
			from: filterValue.from?.toISOString() ?? null,
			to: filterValue.to?.toISOString() ?? null,
		}
	},
	deserialize: (filterValue) => {
		return {
			from: filterValue.from ? new Date(filterValue.from) : undefined,
			to: filterValue.to ? new Date(filterValue.to) : undefined,
		}
	},
})

export const DateRangeNotInFilterModifier = FilterModifier.create<Date, DateRange, SerializedDateRange>({
	id: "date-range-not-in",
	getApplyFn: (filterValue) => {
		const fromTime = filterValue?.from?.getTime()
		const toTime = filterValue?.to?.getTime()
		return (value) => {
			const valueTime = value.getTime()
			if (fromTime !== undefined && fromTime > valueTime) return true
			if (toTime !== undefined && toTime < valueTime) return true
			return false
		}
	},
	render: () => "not in between",
	serialize: (filterValue) => {
		return {
			from: filterValue.from?.toISOString() ?? null,
			to: filterValue.to?.toISOString() ?? null,
		}
	},
	deserialize: (filterValue) => {
		return {
			from: filterValue.from ? new Date(filterValue.from) : undefined,
			to: filterValue.to ? new Date(filterValue.to) : undefined,
		}
	},
})
